import React, { useEffect, useMemo, useRef } from 'react';
import { css, Global } from '@emotion/react';

import {
  LearnerFinalSummaryReport,
  RecommendedResource,
} from 'redux/schemas/app/learner-final-summary';
import CountUp from './count-up';
import ViewToggle, { LearnerTakeawayView } from './view-toggle';
import { printTakeawayNode } from './print-takeaway';

type TakeawayProfessionalProps = {
  report: LearnerFinalSummaryReport;
  learnerFirstName: string;
  courseTitle: string;
  onClose: () => void;
  onOpenResource?: (resource: RecommendedResource) => void;
  view: LearnerTakeawayView;
  onSetView: (view: LearnerTakeawayView) => void;
};

const MAX_COURSE_HIGHLIGHT_SENTENCES = 1;
const MAX_COURSE_HIGHLIGHT_CHARS = 220;
const MAX_HERO_HEADLINE_CHARS = 170;
const MAX_WORK_SUMMARY_SENTENCES = 2;
const MAX_WORK_SUMMARY_CHARS = 320;

const proGlobal = css`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');
  .pro-reveal { opacity: 0; transform: translateY(32px) scale(0.98); transition: opacity 0.7s ease, transform 0.7s ease; }
  .pro-reveal.pro-visible { opacity: 1; transform: none; }
  @media (prefers-reduced-motion: reduce) {
    .pro-reveal { opacity: 1; transform: none; transition: none; }
  }
`;

const blue = '#1457d9';
const purple = '#7a3ff2';
const ink = '#111827';
const muted = '#6b7280';
const line = '#e5e7eb';

const rootStyles = css`
  position: fixed;
  inset: 0;
  z-index: 1080;
  color: ${ink};
  font-family: 'Inter', 'Open Sans', Arial, sans-serif;

  @page {
    margin: 12mm;
  }

  .pro-shell { display: flex; width: 100%; height: 100%; }

  aside {
    flex: 0 0 268px;
    width: 268px;
    height: 100%;
    overflow-y: auto;
    padding: 28px 22px;
    background: linear-gradient(180deg, #ffffff, #f6f8ff);
    border-right: 1px solid ${line};
  }
  .pro-brand { display: flex; gap: 10px; align-items: center; font-weight: 800; font-size: 18px; margin-bottom: 26px; }
  .pro-dot { width: 28px; height: 28px; border-radius: 9px; background: linear-gradient(135deg, ${blue}, ${purple}); flex: 0 0 28px; }
  .pro-nav-title { font-size: 12px; color: ${muted}; text-transform: uppercase; letter-spacing: 0.08em; margin: 14px 0 12px; }
  .pro-nav button { display: flex; align-items: center; gap: 10px; width: 100%; text-align: left; padding: 10px 12px; margin: 4px 0; color: #374151; background: transparent; border: 0; font: inherit; border-radius: 12px; font-size: 14px; cursor: pointer; transition: background 0.15s ease, color 0.15s ease; }
  .pro-nav button:hover { background: #f1f4fb; }
  .pro-nav button.active { background: #eef4ff; color: ${blue}; font-weight: 700; }
  .pro-nav button span { width: 24px; height: 24px; border-radius: 50%; display: grid; place-items: center; border: 1px solid #cfd7e6; font-size: 12px; background: white; flex: 0 0 24px; }
  .pro-nav button.active span { background: ${blue}; color: white; border-color: ${blue}; }
  .pro-note { margin-top: 24px; padding: 16px; border: 1px solid ${line}; border-radius: 16px; background: white; font-size: 13px; color: ${muted}; line-height: 1.45; }

  .pro-main {
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    overflow-y: auto;
    scroll-behavior: smooth;
    background: linear-gradient(180deg, #f7f9ff, #ffffff 45%, #f6f9ff);
  }

  .pro-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 28px;
    border-bottom: 1px solid rgba(229, 231, 235, 0.9);
    background: rgba(255, 255, 255, 0.86);
    backdrop-filter: blur(16px);
  }
  .pro-btn { border: 1px solid ${line}; background: white; color: ${ink}; border-radius: 12px; padding: 10px 16px; font-weight: 700; font-size: 14px; cursor: pointer; }
  .pro-btn:hover { border-color: #cfd7e6; }
  .pro-btn.primary { background: linear-gradient(135deg, ${blue}, ${purple}); color: white; border: 0; }
  .pro-topbar-right { display: flex; align-items: center; gap: 12px; }

  section {
    position: relative;
    padding: clamp(56px, 8vh, 96px) clamp(28px, 6vw, 88px);
    border-bottom: 1px solid rgba(229, 231, 235, 0.7);
  }
  .pro-reveal { max-width: 940px; margin: 0 auto; width: 100%; }
  .pro-final { text-align: center; }
  .pro-final .pro-reveal { max-width: 720px; }
  .pro-final p { margin-left: auto; margin-right: auto; }

  .pro-no { position: absolute; right: 36px; top: 28px; font-size: clamp(46px, 6vw, 92px); font-weight: 900; color: rgba(17, 24, 39, 0.06); letter-spacing: -0.08em; }
  .pro-kicker { color: ${blue}; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; font-size: 13px; margin-bottom: 14px; }
  h1, h2 { margin: 0; letter-spacing: -0.04em; line-height: 1.04; }
  h1 { font-size: clamp(40px, 5vw, 68px); }
  h2 { font-size: clamp(28px, 3.6vw, 46px); }
  p { color: #374151; line-height: 1.65; font-size: 17px; max-width: 720px; }

  .pro-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 18px; margin-top: 36px; }
  .pro-cards3 { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 18px; margin-top: 28px; }
  .pro-card { background: rgba(255, 255, 255, 0.9); border: 1px solid ${line}; border-radius: 20px; padding: 22px; box-shadow: 0 18px 50px rgba(17, 24, 39, 0.06); }
  .pro-card h3 { margin: 0 0 8px; font-size: 18px; letter-spacing: -0.02em; }
  .pro-card p { font-size: 15px; margin: 0; }
  .pro-num { font-size: 44px; font-weight: 900; letter-spacing: -0.05em; color: ${ink}; }
  .pro-stat-label { color: ${muted}; font-size: 14px; margin-top: 6px; }

  .pro-list { margin: 8px 0 0; padding: 0; list-style: none; }
  .pro-list li { position: relative; padding-left: 22px; margin-bottom: 8px; font-size: 15px; line-height: 1.55; color: #374151; }
  .pro-list li::before { content: ''; position: absolute; left: 0; top: 9px; width: 8px; height: 8px; border-radius: 50%; background: ${blue}; }

  .pro-ssc { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 18px; margin-top: 28px; }
  .pro-ssc .pro-card { border-top: 4px solid var(--pro-accent); }
  .pro-ssc h3 { color: var(--pro-accent); }

  .pro-res { display: flex; align-items: center; gap: 14px; width: 100%; text-align: left; cursor: pointer; background: white; border: 1px solid ${line}; border-radius: 16px; padding: 16px; margin-top: 14px; box-shadow: 0 14px 36px rgba(17, 24, 39, 0.05); }
  .pro-res:hover { border-color: #cfe0ff; }
  .pro-res .pro-resic { width: 44px; height: 44px; border-radius: 12px; flex: 0 0 44px; display: grid; place-items: center; color: white; background: linear-gradient(135deg, ${blue}, ${purple}); font-size: 18px; }
  .pro-res .pro-resttl { display: block; font-weight: 700; font-size: 16px; color: ${ink}; }
  .pro-res .pro-rescap { font-size: 14px; color: ${muted}; }
  .pro-tags { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 16px; }
  .pro-tag { font-size: 14px; font-weight: 600; padding: 8px 14px; border-radius: 999px; background: #eef4ff; color: ${blue}; }
  .pro-footnote { margin-top: 24px; font-size: 13px; color: ${muted}; }

  @media (max-width: 820px) {
    aside { display: none; }
    section { padding: 44px 22px; }
    .pro-topbar { padding: 0 16px; }
  }
  @media print {
    position: static;
    aside, .pro-topbar { display: none; }
    .pro-shell, .pro-main { display: block; height: auto; overflow: visible; }
    section { page-break-inside: avoid; background: #fff !important; }
    .pro-reveal { opacity: 1; transform: none; }
  }
`;

/** Subtle per-section backgrounds so every part of the report feels designed. */
const proSectionBackgrounds = [
  'radial-gradient(circle at 80% 16%, rgba(255,204,112,0.30), transparent 30%), radial-gradient(circle at 84% 72%, rgba(122,63,242,0.18), transparent 40%), linear-gradient(135deg, #f4f1ff, #eaf2ff 55%, #ffffff)',
  'linear-gradient(160deg, #eef4ff 0%, #ffffff 62%)',
  'linear-gradient(160deg, #f4f0ff 0%, #ffffff 62%)',
  'linear-gradient(160deg, #eafaf3 0%, #ffffff 62%)',
  'linear-gradient(160deg, #fff6ea 0%, #ffffff 62%)',
  'linear-gradient(160deg, #fdeef4 0%, #ffffff 62%)',
];
const proFinalBackground = 'radial-gradient(circle at 28% 26%, rgba(122,63,242,0.16), transparent 32%), radial-gradient(circle at 76% 30%, rgba(245,158,11,0.20), transparent 32%), linear-gradient(135deg, #ffffff, #eef4ff)';

type SectionDef = {
  id: string;
  label: string;
  when: boolean;
  render: () => React.ReactNode;
};

const normalizeWhitespace = (text: string | null | undefined) => ` ${text || ''} `.replace(/\s+/g, ' ').trim();

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const trimAtWordBoundary = (text: string, maxChars: number) => {
  if (text.length <= maxChars) return text;
  const slice = text.slice(0, maxChars - 3).trimEnd();
  const boundary = slice.lastIndexOf(' ');
  return `${(boundary > 0 ? slice.slice(0, boundary) : slice).trimEnd()}...`;
};

const splitIntoSentences = (text: string) => normalizeWhitespace(text)
  .split(/(?<=[.!?])\s+/)
  .map(sentence => sentence.trim())
  .filter(Boolean);

const compactCourseHighlights = (text: string, courseTitle: string) => {
  const normalized = normalizeWhitespace(text)
    .replace(new RegExp(`^Overview\\s+${escapeRegExp(courseTitle)}\\s+`, 'i'), 'This course ')
    .replace(/^Overview\s+/i, '')
    .replace(/\s*Learning Objectives\s*[-:]\s*/i, '. ');
  if (!normalized) return '';
  const sentences = splitIntoSentences(normalized);
  if (sentences.length === 0) return trimAtWordBoundary(normalized, MAX_COURSE_HIGHLIGHT_CHARS);
  return trimAtWordBoundary(
    sentences.slice(0, MAX_COURSE_HIGHLIGHT_SENTENCES).join(' '),
    MAX_COURSE_HIGHLIGHT_CHARS,
  );
};

const compactLearnerWorkSummary = (
  text: string,
  learnerFirstName: string,
  courseTitle: string,
) => {
  const rewritten = rewriteLearnerFacingText(text, learnerFirstName, courseTitle);
  if (!rewritten) return '';

  const sentences = splitIntoSentences(rewritten);
  if (sentences.length === 0) return trimAtWordBoundary(rewritten, MAX_WORK_SUMMARY_CHARS);

  return trimAtWordBoundary(
    sentences.slice(0, MAX_WORK_SUMMARY_SENTENCES).join(' '),
    MAX_WORK_SUMMARY_CHARS,
  );
};

const rewriteLearnerFacingText = (
  text: string,
  learnerFirstName: string,
  courseTitle: string,
) => {
  let rewritten = normalizeWhitespace(text);
  if (!rewritten) return '';

  if (learnerFirstName) {
    rewritten = rewritten.replace(new RegExp(`${escapeRegExp(learnerFirstName)}'s`, 'g'), 'your');
    rewritten = rewritten.replace(new RegExp(`\\b${escapeRegExp(learnerFirstName)}\\b`, 'g'), 'you');
  }

  if (courseTitle) {
    rewritten = rewritten.replace(
      new RegExp(`Throughout ${escapeRegExp(courseTitle)}, you\\b`, 'i'),
      'Throughout the course, you',
    );
  }

  rewritten = rewritten
    .replace(/^Throughout [^,]+, you\b/i, 'Throughout the course, you')
    .replace(/\bHis\b/g, 'Your')
    .replace(/\bHer\b/g, 'Your')
    .replace(/\bTheir\b/g, 'Your')
    .replace(/\bhis\b/g, 'your')
    .replace(/\bher\b/g, 'your')
    .replace(/\btheir\b/g, 'your')
    .replace(/\bHe\b/g, 'You')
    .replace(/\bShe\b/g, 'You')
    .replace(/\bThey\b/g, 'You')
    .replace(/\bhe\b/g, 'you')
    .replace(/\bshe\b/g, 'you')
    .replace(/\bthey\b/g, 'you')
    .replace(/\bYou is\b/g, 'You are')
    .replace(/\byou is\b/g, 'you are')
    .replace(/\bYou was\b/g, 'You were')
    .replace(/\byou was\b/g, 'you were')
    .replace(/\bYou has\b/g, 'You have')
    .replace(/\byou has\b/g, 'you have')
    .replace(/\bYou engages\b/g, 'You engage')
    .replace(/\byou engages\b/g, 'you engage')
    .replace(/\bYou demonstrates\b/g, 'You demonstrate')
    .replace(/\byou demonstrates\b/g, 'you demonstrate')
    .replace(/\bYou prefers\b/g, 'You prefer')
    .replace(/\byou prefers\b/g, 'you prefer')
    .replace(/\byour strongest asset is your\b/gi, 'your strongest asset is')
    .replace(/\byour greatest strength is your\b/gi, 'your greatest strength is');

  return rewritten;
};

const lowercaseFirstCharacter = (text: string) => {
  if (!text) return '';
  return `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
};

const buildHeroHeadline = (
  finalTakeaway: string,
  learnerFirstName: string,
  courseTitle: string,
) => {
  const directTakeaway = rewriteLearnerFacingText(
    splitIntoSentences(finalTakeaway)[0] || finalTakeaway,
    learnerFirstName,
    courseTitle,
  );
  const compactTakeaway = trimAtWordBoundary(directTakeaway, MAX_HERO_HEADLINE_CHARS);

  if (!compactTakeaway) {
    return learnerFirstName ? `${learnerFirstName}, your strongest asset is how you stayed with it.` : 'Your strongest asset is how you stayed with it.';
  }

  if (!learnerFirstName) return compactTakeaway;

  const headlineBody = /^(you|your)\b/i.test(compactTakeaway)
    ? lowercaseFirstCharacter(compactTakeaway)
    : compactTakeaway;

  return `${learnerFirstName}, ${headlineBody}`;
};

const TakeawayProfessional: React.FC<TakeawayProfessionalProps> = ({
  report,
  learnerFirstName,
  courseTitle,
  onClose,
  onOpenResource,
  view,
  onSetView,
}) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const onPrint = () => {
    void printTakeawayNode(shellRef.current, `${courseTitle} | NovoEd`, 'pro-print-root');
  };

  const sections: SectionDef[] = useMemo(() => {
    const ssc = report.stopStartContinue ?? { stop: [], start: [], continue: [] };
    const counts = report.evidenceCounts ?? { submissionsUsed: 0, rolePlaysUsed: 0, qnaItemsUsed: 0 };
    const recs = report.recommendations ?? { resources: [], topicsToExplore: [] };
    const hasSsc = ssc.stop.length > 0 || ssc.start.length > 0 || ssc.continue.length > 0;
    const hasRecs = (recs.resources?.length ?? 0) > 0 || (recs.topicsToExplore?.length ?? 0) > 0;
    const heroHeadline = buildHeroHeadline(report.finalTakeaway, learnerFirstName, courseTitle);
    const courseHighlights = compactCourseHighlights(report.courseSummary, courseTitle);
    const learnerWorkSummary = compactLearnerWorkSummary(
      report.learnerWorkSummary,
      learnerFirstName,
      courseTitle,
    );

    return [
      {
        id: 'welcome',
        label: 'Welcome',
        when: true,
        render: () => (
          <div className='pro-reveal'>
            <div className='pro-kicker'>Your AI takeaway</div>
            <h1>{heroHeadline}</h1>
            {courseTitle && <h2 style={{ fontSize: 26, marginTop: 16 }}>{courseTitle}</h2>}
          </div>
        ),
      },
      {
        id: 'glance',
        label: 'At a glance',
        when: true,
        render: () => (
          <div className='pro-reveal'>
            <div className='pro-kicker'>Your activity</div>
            <h2>Here is the snapshot.</h2>
            <div className='pro-stats'>
              <div className='pro-card'><CountUp to={counts.submissionsUsed} className='pro-num' /><div className='pro-stat-label'>Submissions</div></div>
              <div className='pro-card'><CountUp to={counts.rolePlaysUsed} className='pro-num' /><div className='pro-stat-label'>Role plays</div></div>
              <div className='pro-card'><CountUp to={counts.qnaItemsUsed} className='pro-num' /><div className='pro-stat-label'>Discussions</div></div>
            </div>
          </div>
        ),
      },
      {
        id: 'learned',
        label: 'Highlights',
        when: !!courseHighlights,
        render: () => (
          <div className='pro-reveal'>
            <div className='pro-kicker'>Course highlights</div>
            <h2>What mattered most.</h2>
            <p style={{ marginTop: 18 }}>{courseHighlights}</p>
          </div>
        ),
      },
      {
        id: 'work',
        label: 'Your work',
        when: !!learnerWorkSummary || report.strengths.length > 0,
        render: () => (
          <div className='pro-reveal'>
            <div className='pro-kicker'>Across your work</div>
            <h2>What stood out.</h2>
            {learnerWorkSummary && <p style={{ marginTop: 18 }}>{learnerWorkSummary}</p>}
            {report.strengths.length > 0 && (
            <div className='pro-cards3'>
              {report.strengths.map((item) => (
                <div className='pro-card' key={item}><h3>Strength</h3><p>{item}</p></div>
              ))}
            </div>
            )}
          </div>
        ),
      },
      {
        id: 'ssc',
        label: 'Stop · start · continue',
        when: hasSsc,
        render: () => (
          <div className='pro-reveal'>
            <div className='pro-kicker'>Your playbook</div>
            <h2>Stop, start, continue.</h2>
            <div className='pro-ssc'>
              <div className='pro-card' style={{ '--pro-accent': '#e24b4a' } as React.CSSProperties}>
                <h3>Stop</h3>
                <ul className='pro-list'>{ssc.stop.map((i) => <li key={i}>{i}</li>)}</ul>
              </div>
              <div className='pro-card' style={{ '--pro-accent': '#10b981' } as React.CSSProperties}>
                <h3>Start</h3>
                <ul className='pro-list'>{ssc.start.map((i) => <li key={i}>{i}</li>)}</ul>
              </div>
              <div className='pro-card' style={{ '--pro-accent': blue } as React.CSSProperties}>
                <h3>Continue</h3>
                <ul className='pro-list'>{ssc.continue.map((i) => <li key={i}>{i}</li>)}</ul>
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'growth',
        label: 'Next level',
        when: report.growthAreas.length > 0,
        render: () => (
          <div className='pro-reveal'>
            <div className='pro-kicker'>Your next level</div>
            <h2>Where to grow.</h2>
            <ul className='pro-list' style={{ marginTop: 18 }}>
              {report.growthAreas.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        ),
      },
      {
        id: 'next',
        label: 'Next steps',
        when: hasRecs,
        render: () => (
          <div className='pro-reveal'>
            <div className='pro-kicker'>Recommended next steps</div>
            <h2>Keep building momentum.</h2>
            {recs.resources?.map((resource) => (
              <button type='button' className='pro-res' key={resource.reason} onClick={() => onOpenResource?.(resource)}>
                <span className='pro-resic'><i className='icon icon-video' aria-hidden='true' /></span>
                <span style={{ flex: 1 }}>
                  <span className='pro-resttl'>{resource.title || 'Revisit this in your course'}</span>
                  <span className='pro-rescap'>{resource.reason}</span>
                </span>
                <i className='icon icon-arrow-right' aria-hidden='true' style={{ color: blue }} />
              </button>
            ))}
            {recs.topicsToExplore?.length > 0 && (
            <div className='pro-tags'>
              {recs.topicsToExplore.map((topic) => <span className='pro-tag' key={topic}>{topic}</span>)}
            </div>
            )}
          </div>
        ),
      },
      {
        id: 'finish',
        label: 'You did it',
        when: true,
        render: () => (
          <div className='pro-reveal pro-final-inner'>
            <div className='pro-kicker'>You did it</div>
            <h1>Congratulations.</h1>
            <p style={{ marginTop: 16 }}>
              {`You completed ${courseTitle || 'your course'}. You showed up, put in the work, and grew.`}
            </p>
            <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button type='button' className='pro-btn primary' onClick={onPrint}>Save as PDF</button>
              <button type='button' className='pro-btn' onClick={onClose}>Back to course</button>
            </div>
            {report.confidenceNotes?.length > 0 && (
            <p className='pro-footnote'>{report.confidenceNotes.join(' ')}</p>
            )}
          </div>
        ),
      },
    ];
  }, [report, learnerFirstName, courseTitle, onOpenResource, onClose]);

  const visibleSections = sections.filter((s) => s.when);

  useEffect(() => {
    const main = mainRef.current;
    const shell = shellRef.current;
    if (!main || !shell) return undefined;

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('pro-visible');
      });
    }, { root: main, threshold: 0.2 });
    main.querySelectorAll('.pro-reveal').forEach((el) => revealObserver.observe(el));

    const navObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          shell.querySelectorAll('.pro-nav button').forEach((a) => {
            a.classList.toggle('active', a.getAttribute('data-target') === entry.target.id);
          });
        }
      });
    }, { root: main, threshold: 0.55 });
    main.querySelectorAll('section').forEach((s) => navObserver.observe(s));

    return () => { revealObserver.disconnect(); navObserver.disconnect(); };
  }, [visibleSections.length]);

  const goTo = (id: string) => {
    mainRef.current?.querySelector(`#${id}`)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div css={rootStyles} ref={shellRef} role='dialog' aria-modal='true' aria-label='Your AI takeaway'>
      <Global styles={proGlobal} />
      <div className='pro-shell'>
        <aside>
          <div className='pro-brand'><span className='pro-dot' /> Your AI takeaway</div>
          <div className='pro-nav-title'>Quick view</div>
          <nav className='pro-nav'>
            {visibleSections.map((s, idx) => (
              <button
                type='button'
                key={s.id}
                data-target={s.id}
                className={idx === 0 ? 'active' : ''}
                onClick={() => goTo(s.id)}
              >
                <span>{String(idx + 1).padStart(2, '0')}</span>
                {s.label}
              </button>
            ))}
          </nav>
          <div className='pro-note'>Generated from your course activity and AI-supported learning data.</div>
        </aside>

        <main className='pro-main' ref={mainRef}>
          <div className='pro-topbar'>
            <button type='button' className='pro-btn' onClick={onClose}>← Back to course</button>
            <div className='pro-topbar-right'>
              <ViewToggle value={view} onChange={onSetView} variant='light' />
              <button type='button' className='pro-btn primary' onClick={onPrint}>Save as PDF</button>
            </div>
          </div>

          {visibleSections.map((s, idx) => {
            const isFinal = s.id === 'finish';
            const background = isFinal
              ? proFinalBackground
              : proSectionBackgrounds[idx % proSectionBackgrounds.length];
            return (
              <section
                key={s.id}
                id={s.id}
                className={isFinal ? 'pro-final' : ''}
                style={{ background }}
              >
                <div className='pro-no'>{String(idx + 1).padStart(2, '0')}</div>
                {s.render()}
              </section>
            );
          })}
        </main>
      </div>
    </div>
  );
};

export default TakeawayProfessional;
